import { createAsyncThunk } from '@reduxjs/toolkit';
import { AUTH } from '../../constant';

export interface Payload {
  email: string;
  password: string;
  hospitalId?: string;
  turnstileToken?: string;
}

export const TRUSTED_DEVICE_TOKEN_KEY = 'trustedDeviceToken';

// Email of the last login attempt in this tab, so the OTP step can file the
// trust token under the right user.
let lastLoginEmail = '';

/**
 * Trust tokens by email. A browser can be trusted by several users; one shared
 * slot let each new user's token replace the last one's. A plain string is a
 * token saved before this, for an unknown user, kept under '*'.
 */
const readTrustedTokens = (): Record<string, string> => {
  const raw = localStorage.getItem(TRUSTED_DEVICE_TOKEN_KEY);
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed;
    }
  } catch {
    // Older plain-string token.
  }
  return { '*': raw };
};

/** The trust token this browser holds for `email`, if any. */
const getStoredDeviceToken = (email: string): string | undefined => {
  try {
    const tokens = readTrustedTokens();
    return tokens[email.trim().toLowerCase()] || tokens['*'] || undefined;
  } catch {
    return undefined;
  }
};

/** Save a trust token from the OTP step for the user who just logged in. */
export const saveDeviceToken = (token: string) => {
  if (!lastLoginEmail) {
    return;
  }
  try {
    const tokens = readTrustedTokens();
    tokens[lastLoginEmail] = token;
    localStorage.setItem(TRUSTED_DEVICE_TOKEN_KEY, JSON.stringify(tokens));
  } catch {
    // Losing the trust token only means OTP is asked again next time.
  }
};

export const loginApi = createAsyncThunk('login', async (payload: Payload, { rejectWithValue }) => {
  try {
    lastLoginEmail = payload.email.trim().toLowerCase();
    const deviceToken = getStoredDeviceToken(payload.email);
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      // A previously-trusted device sends its token on every login attempt — the
      // server treats an absent/invalid/expired one exactly like "not trusted",
      // never an error, so this is safe even for a brand-new browser.
      body: JSON.stringify({ ...payload, deviceToken }),
    };
    let res = await fetch(AUTH.LOGIN_URL, requestOptions);
    const data = await res.json();

    if (!res.ok) {
        return rejectWithValue(data);
    }

    return data;
  } catch (error) {
    return rejectWithValue(error);
  }
});
